import { daysAhead, daysAgo } from './dataset';

// Local Social, Reporting, AI & Automations and Calendar pages.
// (Posts & Showcases makes no data calls on load; it renders from its own static cards.)
const DANA = 'dana@brightsidecoffee.com';
const MARC = 'marc@brightsidecoffee.com';
const PRIYA = 'priya@brightsidecoffee.com';
const TOM = 'tom@brightsidecoffee.com';
const ALIA = 'alia@brightsidecoffee.com';
const NAMES = {
	[DANA]: 'Dana Whitfield',
	[MARC]: 'Marc Oyelaran',
	[PRIYA]: 'Priya Raman',
	[TOM]: 'Tom Beckett',
	[ALIA]: 'Alia Nasser'
};

const people = (...emails) => emails.map(email => ({
	name: NAMES[email],
	email
}));

const pad = n => String(n).padStart(2, '0'); // Calendar scheduleTime is "DD-MM-YYYY HH:mm:ss", not ISO.


const calendarTime = iso => {
	const d = new Date(iso);
	return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
};

const isoDate = iso => iso.slice(0, 10);

const calendarEvent = (id, daysFromNow, extra) => {
	const at = daysAhead(daysFromNow);
	return {
		requestId: `req-cal-${id}`,
		source: 'google',
		calendarDate: at,
		status: 'SCHEDULED',
		total: 5,
		errors: 0,
		timeScheduleType: 'customTime',
		scheduleTime: calendarTime(at),
		...extra
	};
};

const widget = (id, name, creator, extra) => ({
	widgetId: id,
	widgetName: name,
	creatorId: creator,
	createTime: daysAgo(60),
	updateTime: daysAgo(10),
	accessToken: `${id}-access`,
	codeBlock: `<div id="lc-review-widget" data-widget-id="${id}"></div>`,
	ratingValues: [4, 5],
	sources: ['google', 'facebook'],
	locations: [],
	entityTag: {},
	pinnedReviews: [],
	blockedReviews: [],
	maximumReviewCount: 20,
	reviewsPerPage: 5,
	maximumCharacterLength: 160,
	requireComments: true,
	presentComments: true,
	headerText: 'Our Reviews',
	headerTitle: false,
	reviewerImage: true,
	starIconColor: '#f5a623',
	fontFamily: 'Open Sans',
	reviewOrder: 'desc',
	showDate: true,
	showSourceLogo: true,
	isResponsive: true,
	dateStructure: 'preciseDate',
	dateFormat: 'MMM DD, YYYY',
	dateRange: {
		from: isoDate(daysAgo(180)),
		to: isoDate(daysAgo(0))
	},
	titleFontSize: '32px',
	reviewerFontSize: '20px',
	commentFontSize: '14px',
	...extra
}); // Report recipients' names must be two words (initials are taken from them).


const report = (id, name, owner, recipients, activeFilters, type, ago) => ({
	reportId: id,
	reportName: name,
	userEmail: owner,
	createdTime: daysAgo(ago + 40),
	updatedTime: daysAgo(ago),
	superAdminUserId: '',
	emailRecepient: recipients,
	userData: people(...recipients),
	activeFilters,
	type,
	isDeletedFilterTag: false
});

const agent = (id, name, description, extra) => ({
	agentId: id,
	agentName: name,
	agentDescription: description,
	agentIcon: null,
	isDefault: false,
	locationGroupIds: [],
	updateTime: daysAgo(7),
	language: 'English',
	polyLingual: false,
	temperature: 0.4,
	hasLocationContext: true,
	domainName: 'brightsidecoffee',
	...extra
});

const rule = (id, text, triggerSource, trigger, isActivated, creatorName, actions, ago) => ({
	ruleId: id,
	profileId: 'brightside',
	rule: text,
	triggerSource,
	trigger,
	isActivated,
	creatorName,
	updateTime: daysAgo(ago),
	conditions: [],
	actions
});

const RULES = [rule('rule-301', 'When a <b>review</b> is <b>received</b> with <b>1–2 stars</b>, <b>assign</b> to <b>Priya Raman</b>', 'REVIEW', 'CREATED', true, 'Dana Whitfield', [{
	actionType: 'ASSIGN_USER',
	assignedUsers: [PRIYA]
}], 4), rule('rule-302', 'When a <b>review</b> is <b>received</b> mentioning <b>wait time</b>, <b>assign tag</b> <b>Service speed</b>', 'REVIEW', 'CREATED', true, 'Marc Oyelaran', [{
	actionType: 'ASSIGN_TAG',
	reviewCategory: 'Service speed'
}], 13), rule('rule-303', 'When a <b>review</b> is <b>updated</b>, <b>notify</b> <b>Tom Beckett</b>', 'REVIEW', 'UPDATED', true, 'Tom Beckett', [{
	actionType: 'NOTIFY',
	users: [TOM]
}], 27), rule('rule-304', 'When a <b>Google listing edit</b> is <b>received</b>, <b>auto-reject update</b>', 'GOOGLE_EDIT', 'CREATED', true, 'Alia Nasser', [{
	actionType: 'AUTO_REJECT_GOOGLE_EDIT'
}], 11), rule('rule-305', 'When a <b>review</b> is <b>received</b> at <b>Harbour Point</b>, <b>assign team</b> <b>Reviews Desk</b>', 'REVIEW', 'CREATED', false, 'Priya Raman', [{
	actionType: 'ASSIGN_TEAM',
	teamId: 'team-reviews-desk'
}], 53)];

const withData = list => Object.assign(list, {
	status: 200,
	data: list.slice(),
	hasMore: false
});

export default {
	// Calendar — dates are relative to today so they land in the default two-week view.
	'/getCalenderEvents': () => {
		const events = [calendarEvent(101, 1, {
			eventType: 'POST',
			workflowType: 'oneToMany'
		}), calendarEvent(102, 3, {
			eventType: 'POST',
			workflowType: 'bulkUpload',
			source: 'facebook',
			total: 3
		}), calendarEvent(103, 4, {
			eventType: 'IMAGE',
			imageType: 'COVER',
			total: 4
		}), calendarEvent(104, 7, {
			eventType: 'UPDATE',
			updatedField: 'description',
			updateMap: {
				description: 'Fall menu launch: pumpkin oat latte and maple scones now at every Brightside Coffee.'
			}
		}), calendarEvent(105, 8, {
			eventType: 'UPDATE',
			updatedField: 'regularHours',
			total: 2,
			updateMap: {
				regularHours: {
					periods: [{
						openDay: 'SATURDAY',
						openTime: '08:00',
						closeTime: '16:00'
					}, {
						openDay: 'SUNDAY',
						openTime: '08:00',
						closeTime: '14:00'
					}]
				}
			}
		}), calendarEvent(106, 10, {
			eventType: 'POST',
			workflowType: 'oneToMany',
			timeScheduleType: 'immediate'
		})];
		return {
			status: 200,
			total: events.length,
			events
		};
	},
	// Review Showcase
	'/getWidgets': () => {
		const widgetList = [widget('wdg-201', 'Riverside Plaza Homepage', DANA, {
			locations: [{
				locationId: 'loc-riverside',
				locationName: 'Riverside Plaza'
			}],
			starIconColor: '#ff7a00'
		}), widget('wdg-202', 'Oregon Locations Carousel', MARC, {
			ratingValues: [5],
			sources: ['google'],
			locations: [{
				locationId: 'loc-northgate',
				locationName: 'Northgate Center'
			}, {
				locationId: 'loc-westfield',
				locationName: 'Westfield Row'
			}],
			headerText: 'What Oregon Says',
			headerTitle: true,
			fontFamily: 'Lato',
			reviewsPerPage: 3,
			dateStructure: 'reviewAge'
		}), widget('wdg-203', 'Grand Reopening Landing Page', PRIYA, {
			sources: ['google', 'yelp'],
			locations: [{
				locationId: 'loc-harbour',
				locationName: 'Harbour Point'
			}],
			reviewerImage: false,
			fontFamily: 'Roboto',
			reviewOrder: 'asc',
			showSourceLogo: false
		}), widget('wdg-204', 'Loyalty Promo Footer', ALIA, {
			ratingValues: [3, 4, 5],
			locations: [{
				locationId: 'loc-elm',
				locationName: 'Elm & 4th'
			}],
			headerText: 'Loved by Regulars',
			headerTitle: true,
			starIconColor: '#2e7d32',
			fontFamily: 'Montserrat',
			requireComments: false,
			showDate: false,
			maximumCharacterLength: 120
		})];
		return {
			status: 200,
			total: widgetList.length,
			widgetList
		};
	},
	// Reports & Notices — every report needs an activeFilters array.
	'/getReportConfiguration': () => {
		const reports = [report('rpt-1001', 'Weekly Review Digest', DANA, [DANA, MARC], ['Group', 'Rating'], 'REVIEW', 6), report('rpt-1002', 'Monthly Performance Dashboard', PRIYA, [PRIYA], ['Organization'], 'DASHBOARD', 11), report('rpt-1003', 'Listing Accuracy Export', TOM, [TOM, ALIA], [], 'LOCATION', 14), report('rpt-1004', 'Low-Rating Alerts – Washington', MARC, [MARC, DANA, PRIYA], ['Geography', 'Saved Filter'], 'REVIEW', 3), report('rpt-1005', 'Google Suggested Edits Notice', ALIA, [ALIA], ['Location'], 'NOTIFICATION', 8)];
		return {
			status: 200,
			total: reports.length,
			reports
		};
	},
	// Data Studio
	'/getMailedReportDetails': () => {
		const list = [{
			reportName: 'Reviews – Riverside Plaza – Q3',
			mailedUsers: [DANA],
			createTime: daysAgo(4),
			downloadType: 'REVIEW',
			downloadLink: ''
		}, {
			reportName: 'Group Export – Washington cafés',
			mailedUsers: [MARC, PRIYA],
			createTime: daysAgo(6),
			downloadType: 'LOCATION_FILTER_TAG',
			downloadLink: ''
		}, {
			reportName: 'Post Insights – Fall menu launch',
			mailedUsers: [ALIA],
			createTime: daysAgo(9),
			downloadType: 'LOCALPOSTINSIGHTS',
			downloadLink: ''
		}, {
			reportName: 'Merchant Photos – Harbour Point',
			mailedUsers: [TOM],
			createTime: daysAgo(13),
			downloadType: 'MERCHANT_PHOTO',
			downloadLink: ''
		}, {
			reportName: 'Business Profile Audit – All locations',
			mailedUsers: [DANA],
			createTime: daysAgo(17),
			downloadType: 'GMB_LOCATION_AUDIT',
			downloadLink: ''
		}];
		return {
			status: 200,
			total: list.length,
			list
		};
	},
	// Data Studio maps over the list; Audit Logs reads { data, hasMore }.
	'/getProfileAudits': () => withData([{
		auditId: 'aud-501',
		isAutomatic: true,
		fetchType: 'LOCATION',
		name: 'Brightside Coffee',
		count: 5,
		auditTime: daysAgo(1)
	}, {
		auditId: 'aud-502',
		isAutomatic: false,
		fetchType: 'ACCOUNT',
		name: 'Riverside Plaza',
		count: 1,
		auditTime: daysAgo(5)
	}, {
		auditId: 'aud-503',
		isAutomatic: true,
		fetchType: 'LOCATION',
		name: 'Brightside Coffee',
		count: 5,
		auditTime: daysAgo(8)
	}, {
		auditId: 'aud-504',
		isAutomatic: false,
		fetchType: 'ACCOUNT',
		name: 'Northgate Center',
		count: 1,
		auditTime: daysAgo(12)
	}]),
	'/getProfileTokens': () => ({
		status: 200,
		tokens: [{
			tokenId: 'tok-1',
			keyId: 'key-1',
			keyName: 'POS sync',
			maskedToken: 'lc_live_••••••••3f9a',
			createdAt: isoDate(daysAgo(72)),
			status: 'ACTIVE'
		}, {
			tokenId: 'tok-2',
			keyId: 'key-2',
			keyName: 'BI dashboard',
			maskedToken: 'lc_live_••••••••a71c',
			createdAt: isoDate(daysAgo(27)),
			status: 'ACTIVE'
		}, {
			tokenId: 'tok-3',
			keyId: 'key-3',
			keyName: 'Legacy export',
			maskedToken: 'lc_live_••••••••0d2e',
			createdAt: isoDate(daysAgo(216)),
			status: 'REVOKED'
		}]
	}),
	// AI Agents — an agent named 'Default' is the house voice.
	'/getAllAIAgents': () => {
		const agents = [agent('agt-1', 'Default', 'House voice for all Brightside Coffee replies.', {
			isDefault: true,
			temperature: 0.3,
			updateTime: daysAgo(12)
		}), agent('agt-2', 'Warm Barista', 'Friendly, first-name replies for 4–5 star reviews.', {
			locationGroupIds: ['grp-wa-cafes'],
			temperature: 0.6,
			updateTime: daysAgo(7)
		}), agent('agt-3', 'Service Recovery', 'Apologetic, offers a follow-up for 1–2 star reviews.', {
			locationGroupIds: ['grp-or-cafes'],
			temperature: 0.2,
			updateTime: daysAgo(10)
		}), agent('agt-4', 'Multilingual Host', 'Replies in the reviewer’s language.', {
			locationGroupIds: ['grp-flagships'],
			polyLingual: true,
			hasLocationContext: false,
			updateTime: daysAgo(24)
		}), agent('agt-5', 'Loyalty Promo', 'Mentions the loyalty programme when relevant.', {
			temperature: 0.5,
			hasLocationContext: false,
			updateTime: daysAgo(3)
		})];
		return {
			status: 200,
			total: agents.length,
			agents
		};
	},
	// Automations — hits must be a list.
	'/getAutomationRules': () => ({
		status: 200,
		total: RULES.length,
		activeCount: RULES.filter(r => r.isActivated).length,
		reviewTypeCount: RULES.filter(r => r.triggerSource === 'REVIEW').length,
		googleEditTypeCount: RULES.filter(r => r.triggerSource === 'GOOGLE_EDIT').length,
		googleQATypecount: 0,
		hits: RULES
	})
};